import React, { useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { httpRequest } from "../../../../services/http";
import CardWrapper from "../../CardWrapper";
import Card from "../../Card";
import {
  getApiUrl,
  getGenome,
  getLoading,
  getProteinViewerData,
  getQueryType,
  getUserQuery,
  getVariantSpec,
  getVarsomeHost,
} from "../../../../state/selectors/general.selectors";
import {
  getEnsemblTranscripts,
  getTranscripts,
} from "../../../3d-protein-viewer/selectors/ProteinViewer.selectors";
import {
  setProteinViewerDataToDefault,
  setProteinViewerError,
  setProteinViewerResults,
  setProteinViewerTranscripts,
} from "../../../../state/actions/genericActions";
import {
  ensemblTranscriptsType,
  proteinViewerDataType,
  refGenomeType,
  transcriptsType,
} from "./types/types";
import { areEqual } from "../../../../common/helpers/helpers";
import { getClinicalRowId } from "../../../../state/selectors/CardsUI.selectors";
import { getSelectedGeneIndexClinical } from "../../../../state/selectors/CardsUiClinicalSelectors";

const ProteinViewerCard = ({
  card,
  apiUrl,
  ensemblTranscripts,
  transcripts,
  refGenome,
  stateQueryType,
  setProteinResultsAction,
  proteinViewerData,
  setProteinViewerErrorAction,
  setProteinViewerTranscriptsAction,
  clinicalRowId,
  clinicalGeneIndex,
}) => {
  const {
    extraProps: { layout },
  } = card;

  const queryType = useMemo(
    () => layout || stateQueryType,
    [layout, stateQueryType]
  );

  const { error, results, finalTranscripts } = proteinViewerData;

  const cardTitle = (
    <div className="tw-flex tw-items-baseline">
      <span className="tw-mr-1">{card.title}</span>
      <i className="fa fa-cube tw-text-blue-500" />
    </div>
  );
  const getCanonicalTranscripts = (t) => {
    let canonicals;
    if (queryType === "gene") {
      if (!t || t.length === 0) return null;
      const c = t.filter((transcript) => transcript.canonical);
      canonicals = c.filter((o) => o.name.startsWith("ENST"));
    } else {
      if (!t || t[0].items.length === 0) return null;
      canonicals = t[0].items.filter((transcript) => transcript?.canonical);
    }
    if (canonicals.length > 1) {
      const minimumSupport = Math.min(
        ...canonicals.map((item) => parseInt(item.ensembl_support_level, 10))
      );
      return canonicals.filter(
        (item) => parseInt(item.ensembl_support_level, 10) === minimumSupport
      );
    }
    return canonicals;
  };

  useEffect(() => {
    if (!ensemblTranscripts && !transcripts) return;
    if (!transcripts && queryType === "gene") return;
    const trans = queryType === "gene" ? transcripts : ensemblTranscripts;
    if (!finalTranscripts?.length)
      setProteinViewerTranscriptsAction(getCanonicalTranscripts(trans));
  }, [
    ensemblTranscripts,
    transcripts,
    queryType,
    clinicalRowId,
    clinicalGeneIndex,
  ]);

  const fetchProteinViewerInfo = () => {
    const { id: genomeId } = refGenome;
    const { name: accesionId } = finalTranscripts?.[0];
    // Working example https://beta-api.varsome.com/3D-protein-model/1038/ENST00000358273
    httpRequest
      .get(`${apiUrl}/3D-protein-model/${genomeId}/${accesionId}`)
      .then((response) => {
        setProteinResultsAction(response.data);
      })
      .catch((err) => {
        console.error(err);
        setProteinViewerErrorAction(true);
      });
  };

  useEffect(() => {
    if (finalTranscripts?.length) {
      fetchProteinViewerInfo();
    }
    return () => {};
  }, [finalTranscripts]);

  return (
    <CardWrapper
      {...card}
      isDisabled={error || !results}
      title={cardTitle}
      humanTitle={card.title}
    />
  );
};

const mapStateToProps = (state) => ({
  refGenome: getGenome(state),
  apiUrl: getApiUrl(state),
  ensemblTranscripts: getEnsemblTranscripts(state),
  transcripts: getTranscripts(state),
  loadingState: getLoading(state),
  varsomeHost: getVarsomeHost(state),
  stateQueryType: getQueryType(state),
  variantSpec: getVariantSpec(state),
  proteinViewerData: getProteinViewerData(state),
  query: getUserQuery(state),
  clinicalRowId: getClinicalRowId(state),
  clinicalGeneIndex: getSelectedGeneIndexClinical(state),
});

const mapDispatchToProps = (dispatch) => ({
  setProteinResultsAction: (params) =>
    dispatch(setProteinViewerResults(params)),
  setProteinViewerErrorAction: (params) =>
    dispatch(setProteinViewerError(params)),
  setProteinViewerTranscriptsAction: (params) =>
    dispatch(setProteinViewerTranscripts(params)),
  setProteinViewerDataDefaultState: (params) =>
    dispatch(setProteinViewerDataToDefault(params)),
});

ProteinViewerCard.propTypes = {
  card: PropTypes.instanceOf(Card).isRequired,
  setProteinViewerTranscriptsAction: PropTypes.func.isRequired,
  setProteinResultsAction: PropTypes.func.isRequired,
  setProteinViewerErrorAction: PropTypes.func.isRequired,
  proteinViewerData: proteinViewerDataType,
  refGenome: refGenomeType,
  apiUrl: PropTypes.string,
  ensemblTranscripts: ensemblTranscriptsType,
  transcripts: transcriptsType,
  loadingState: PropTypes.bool,
  varsomeHost: PropTypes.string,
  stateQueryType: PropTypes.string,
  variantSpec: PropTypes.string,
  clinicalRowId: PropTypes.number,
  clinicalGeneIndex: PropTypes.number,
};
ProteinViewerCard.defaultProps = {
  proteinViewerData: PropTypes.shape({
    results: null,
    error: false,
    finalTranscripts: [],
  }),
  refGenome: null,
  apiUrl: "",
  ensemblTranscripts: null,
  transcripts: null,
  loadingState: false,
  varsomeHost: "",
  stateQueryType: null,
  variantSpec: "",
  clinicalRowId: 0,
  clinicalGeneIndex: 0,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(React.memo(ProteinViewerCard, areEqual));
